import React from "react";
import "./NavigationBar.scss";
import {
    FacebookOutlined,
    FileOutlined,
    FormOutlined,
    HomeOutlined,
    InfoCircleOutlined,
    InstagramOutlined,
    LinkedinOutlined,
    MailOutlined,
    MoreOutlined,
} from "@ant-design/icons";
import {NavLink} from "react-router-dom";
import {Nullable} from "@data-force/app";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {connect} from "react-redux";
import {Tooltip} from "antd";
import Logo from "../common/Logo/Logo";
import HistoryParams from "../../interfaces/historyParams";
import {getHistoryParams} from "../../utils/common";
import StoreState from "../../interfaces/store/storeState";
import {RoutesMap} from "../../interfaces/common";
import {Routes} from "../../utils/constants";
import {SetActiveJobTypeAction, SetFilterTypeAction, SetJobTestTypeAction} from "../../interfaces/store/actions/job";
import {setActiveJobType, setFilterType, setJobTestType} from "../../store/actions/job";
import {ContentType, FilterType} from "../../enums/enums";
import {SetActiveTestTypeAction} from "../../interfaces/store/test";
import {setActiveTestType} from "../../store/actions/test";
import {TikTokIcon} from "../../assets/Icons";

interface StateProps {
    pmUser: boolean
    facebookLink: string
    tikTokLink: string
    instagramLink: string
    linkedInLink: string
    isMobile: boolean
    translate: TranslateFunction
}

interface DispatchProps {
    setJobTestType(payload: boolean): SetJobTestTypeAction

    setActiveTestType(payload: string): SetActiveTestTypeAction

    setFilterType(payload: string): SetFilterTypeAction

    setActiveJobType(payload: string): SetActiveJobTypeAction
}

type Props = StateProps & DispatchProps;

class NavigationBar extends React.Component<Props> {
    private static readonly translationPath: string = "components.NavigationBar.NavigationBar";

    /**
     * Based on isTest property we load necessary actions for jobs/tests views
     * @param {boolean} isTest
     */
    setViewData(isTest: boolean): void {
        if (isTest) {
            this.props.setActiveTestType(ContentType.AVAILABLE);
        } else {
            this.props.setActiveJobType(ContentType.AVAILABLE);
        }
        this.props.setFilterType(FilterType.CLAIMABLE);
        this.props.setJobTestType(isTest);
    }

    render() {
        const {
            pmUser, facebookLink, instagramLink, linkedInLink, tikTokLink, isMobile, translate,
        }: Props = this.props;
        const historyParams: Nullable<HistoryParams> = getHistoryParams();
        const {translationPath}: typeof NavigationBar = NavigationBar;
        let qualificationsLocalization: string = translate(`${translationPath}.qualifications.text`) as string;
        const {
            JOBS, FAQ, SUPPORT, QUALIFICATIONS, MORE,
        }: RoutesMap = Routes;

        if (isMobile && qualificationsLocalization.length > 6) { // Limited localization for qualifications on six characters on mobile devices
            qualificationsLocalization = `${qualificationsLocalization.slice(0, 6)}.`;
        }

        return (
            <nav className="df-nav">
                <div className="df-nav-upper">
                    <div className="df-nav-logo-container">
                        <Logo dark={false}/>
                    </div>
                    <ul>
                        {!pmUser && (
                            <li className="df-nav-item">
                                <NavLink to={`/${historyParams?.idpId}/`} replace exact>
                                    <HomeOutlined aria-hidden/>
                                    <Translate id={`${translationPath}.home.text`}/>
                                </NavLink>
                            </li>
                        )}
                        <li className="df-nav-item">
                            <NavLink
                                onClick={this.setViewData.bind(this, false)}
                                to={`/${historyParams?.idpId}/${JOBS}`}
                                replace
                            >
                                <FileOutlined aria-hidden/>
                                <Translate id={`${translationPath}.jobs.text`}/>
                            </NavLink>
                        </li>
                        <li className="df-nav-item">
                            <NavLink
                                onClick={this.setViewData.bind(this, true)}
                                to={`/${historyParams?.idpId}/${QUALIFICATIONS}`}
                                replace
                            >
                                <FormOutlined aria-hidden/>
                                {qualificationsLocalization}
                            </NavLink>
                        </li>
                        {isMobile ? (
                            <li className="df-nav-item">
                                <NavLink to={`/${historyParams?.idpId}/${MORE}`} replace>
                                    <MoreOutlined rotate={90} aria-hidden/>
                                    <Translate id={`${translationPath}.more.text`}/>
                                </NavLink>
                            </li>
                        ) : (
                            <>
                                <li className="df-nav-item">
                                    <NavLink to={`/${historyParams?.idpId}/${FAQ}`} replace>
                                        <InfoCircleOutlined aria-hidden/>
                                        <Translate id={`${translationPath}.faq.text`}/>
                                    </NavLink>
                                </li>
                                <li className="df-nav-item">
                                    <NavLink to={`/${historyParams?.idpId}/${SUPPORT}`} replace>
                                        <MailOutlined aria-hidden/>
                                        <Translate id={`${translationPath}.support.text`}/>
                                    </NavLink>
                                </li>
                            </>
                        )}
                    </ul>
                </div>
                <div className="df-nav-lower">
                    <Tooltip
                        placement="topLeft"
                        arrowPointAtCenter
                        title={<Translate id={`${translationPath}.facebookPage.text`}/>}
                    >
                        <a href={facebookLink} target="_blank" rel="noopener noreferrer">
                            <FacebookOutlined aria-hidden/>
                        </a>
                    </Tooltip>
                    <Tooltip
                        placement="topLeft"
                        arrowPointAtCenter
                        title={<Translate id={`${translationPath}.tikTokPage.text`}/>}
                    >
                        <a href={tikTokLink} target="_blank" rel="noopener noreferrer">
                            <TikTokIcon aria-hidden/>
                        </a>
                    </Tooltip>
                    <Tooltip
                        placement="topLeft"
                        arrowPointAtCenter
                        title={<Translate id={`${translationPath}.instagramPage.text`}/>}
                    >
                        <a href={instagramLink} target="_blank" rel="noopener noreferrer">
                            <InstagramOutlined aria-hidden/>
                        </a>
                    </Tooltip>
                    <Tooltip
                        placement="topLeft"
                        arrowPointAtCenter
                        title={<Translate id={`${translationPath}.linkedInPage.text`}/>}
                    >
                        <a href={linkedInLink} target="_blank" rel="noopener noreferrer">
                            <LinkedinOutlined aria-hidden/>
                        </a>
                    </Tooltip>
                </div>
            </nav>
        );
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        pmUser: state.user.pmUser,
        facebookLink: state.serviceProperties.facebookLink,
        tikTokLink: state.serviceProperties.tikTokLink,
        instagramLink: state.serviceProperties.instagramLink,
        linkedInLink: state.serviceProperties.linkedInLink,
        isMobile: state.mobile.isMobile,
        translate: getTranslate(state.localize),
    };
}

const mapDispatchToProps: DispatchProps = {
    setJobTestType,
    setActiveTestType,
    setActiveJobType,
    setFilterType,
};

export default connect(mapStateToProps, mapDispatchToProps)(NavigationBar);
